import type { DonationPaymentMethod } from './donation.js';

/**
 * The payment acquirers — docs/decisions/0053-a-payment-account-belongs-to-an-organization-and-a-link-is-drawn-on-one.md:
 * Stripe and PayPal, which the organization uses in Europe today, and NestPay
 * behind a Turkish bank's virtual POS, which is deferred. Which bank's POS is
 * still open; it decides only which `pos` account a NestPay capture names, so
 * nothing here names a bank. PayPal is not a card acquirer and its captures
 * settle a `paypal` donation rather than a `card` one, which is why the payment
 * method below is a map.
 */
export const CARD_PROVIDERS = ['nestpay', 'stripe', 'paypal'] as const;

export type CardProvider = (typeof CARD_PROVIDERS)[number];

/**
 * The kind of account each provider's captures are kept in. A Stripe balance
 * is not a POS terminal, which is why `account_kind` has both —
 * docs/plans/05-finance-reconciliation.md §5.1.
 */
export const CARD_PROVIDER_ACCOUNT_KIND: Readonly<
  Record<CardProvider, 'pos' | 'stripe' | 'paypal'>
> = {
  nestpay: 'pos',
  stripe: 'stripe',
  paypal: 'paypal',
};

/**
 * The payment method each provider's capture settles. A checkout creates its
 * donation with this method before the provider is asked, so a capture naming a
 * bank-transfer or cash donation is a defect in the caller, not a payment to
 * record. PayPal settles `paypal` and not `card`: taken as `card` every PayPal
 * capture would be refused `RECONCILIATION_PROVIDER_PAYMENT_METHOD_MISMATCH`.
 */
export const CARD_CAPTURE_PAYMENT_METHOD: Readonly<Record<CardProvider, DonationPaymentMethod>> = {
  nestpay: 'card',
  stripe: 'card',
  paypal: 'paypal',
};

export function isCardProvider(value: string): value is CardProvider {
  return (CARD_PROVIDERS as readonly string[]).includes(value);
}

/** The most of a card number the PCI DSS display rule lets show: the first six and the last four. */
const MASKED_CARD_NUMBER = /^[0-9]{0,6}[*xX•]{3,}[0-9]{0,4}$/;

/**
 * Whether a card number is masked as a provider returns it — `454359******1234`,
 * `**** **** **** 4242` — and so safe to keep. A string with more than ten
 * digits, or with no mask run between them, is refused: no card number reaches
 * BTSis, and the legacy wrote whole ones into a free-text column —
 * docs/decisions/0042 *Consequences*.
 */
export function isMaskedCardNumber(value: string): boolean {
  const compact = value.replace(/[\s-]/g, '');
  if (!MASKED_CARD_NUMBER.test(compact)) return false;
  return compact.replace(/[^0-9]/g, '').length <= 10;
}
